import "./FilePickerView.styl";
import { supabaseClient as supabase } from "../auth/supabase.service.js";

export default function FilePickerView({ container = "body", message = {} } = {}) {
  const { bad, info } = message;
  const d3 = window.d3;

  let panelSel = null;
  let overlaySel = null;
  let listSel = null;
  let currentFiles = [];
  let currentOnSelect = () => {};

  function selectFile(d) {
    if (!d?.url) {
      if (info) info("This file has no public url");
      return;
    }
    const payload = { url: d.url, name: d.name || d.url };
    currentOnSelect(payload);
    close();
  }

  async function fetchFiles() {
    try {
      const { data, error } = await supabase
        .from("files")
        .select("id, name, url, content_type, size, created_at, user_id, public")
        .order("created_at", { ascending: false });
      if (error) {
        if (bad) bad("Error fetching files: " + JSON.stringify(error));
        return [];
      }
      return data || [];
    } catch (err) {
      if (bad) bad("Error fetching files: " + err.message);
      return [];
    }
  }

  function formatSize(bytes) {
    if (bytes == null) return "";
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
    return (bytes / (1024 * 1024)).toFixed(2) + " MB";
  }

  function ensureFontFace(id, url, formatHint) {
    if (!url) return null;
    const styleId = `fp-font-${id}`;
    if (document.getElementById(styleId)) return styleId;
    const fmt = formatHint ? ` format("${formatHint}")` : "";
    const css = `
      @font-face {
        font-family: "${styleId}";
        src: url("${url}")${fmt};
        font-weight: normal;
        font-style: normal;
        font-display: swap;
      }
    `;
    const s = document.createElement("style");
    s.id = styleId;
    s.appendChild(document.createTextNode(css));
    document.head.appendChild(s);
    return styleId;
  }

  function guessFormatFromName(name) {
    if (!name) return null;
    const ext = name.split(".").pop().toLowerCase();
    switch (ext) {
      case "woff2": return "woff2";
      case "woff": return "woff";
      case "ttf": return "truetype";
      case "otf": return "opentype";
      default: return null;
    }
  }

  async function render() {
    const root = d3.select(container || "body");

    overlaySel = root.append("div").attr("class", "file-picker-overlay");
    overlaySel.on("click", close);

    panelSel = overlaySel.append("div").attr("class", "file-picker-panel");
    panelSel.on("click", (event) => {
      event.stopPropagation();
    })

    const header = panelSel.append("div").attr("class", "file-picker-header");
    header.append("strong").text("Insert file");
    header.append("div").attr("class", "closecross").text("✕").on("click", close);

    const search = panelSel.append("input")
      .attr("type", "search")
      .attr("class", "file-picker-search")
      .attr("placeholder", "filter by name...");

    listSel = panelSel.append("div").attr("class", "file-picker-list");

    search.on("input", () => {
      const q = (search.property("value") || "").trim().toLowerCase();
      renderList(currentFiles, q);
    });

    d3.select(window).on("keydown.filePicker", (event) => {
      if (event.key === "Escape") close();
    });

    currentFiles = await fetchFiles();
    renderList(currentFiles, "");
  }

  function renderList(files = [], filter = "") {
    const shown = (files || []).filter(f => {
      if (!filter) return true;
      return (f.name || "").toLowerCase().includes(filter);
    });

    const items = listSel.selectAll(".file-picker-item")
      .data(shown, d => d.id);

    items.exit().remove();

    const enter = items.enter().append("div").attr("class", "file-picker-item");

    enter.append("div").attr("class", "file-picker-thumb");

    const meta = enter.append("div").attr("class", "file-picker-meta");
    meta.append("div").attr("class", "file-picker-name").text(d => d.name || "(untitled)");
    meta.append("div").attr("class", "file-picker-sub").text(d => `${formatSize(d.size)} • ${d.content_type || ""} • ${d.user_id || ""}`);

    const actions = enter.append("div").attr("class", "file-picker-actions");
    actions.append("div").attr("class", "button").text("Select").on("click", function(event, d) {
      event.stopPropagation();
      selectFile(d);
    });

    const all = enter.merge(items);
    all.each(function(d) {
      const row = d3.select(this);
      const t = row.select(".file-picker-thumb");
      t.html(null);

      if (d.content_type && d.content_type.startsWith("image/") && d.url) {
        t.append("img").attr("src", d.url);
      } else if ((d.content_type && d.content_type.includes("font")) || /\.(ttf|otf|woff2?|eot)$/i.test(d.name)) {
        if (d.url) {
          const fmt = guessFormatFromName(d.name);
          const fontFamily = ensureFontFace(d.id, d.url, fmt);
          t.append("span").attr("class", "font-preview").text("Aa").style("font-family", fontFamily || "inherit");
        } else {
          t.text("font");
        }
      } else if (d.content_type === "application/pdf" && d.url) {
        t.append("a").attr("href", d.url).attr("target", "_blank").text("PDF");
      } else {
        const ext = (d.name || "").split(".").pop() || "file";
        t.append("div").text(ext).style("padding", "6px 10px").style("background", "#f0f0f0").style("border-radius", "4px");
      }

      row.on("click", () => {
        selectFile(d);
      });
    });

    if (!shown.length) {
      listSel.html("");
      listSel.append("div").attr("class", "file-picker-empty").text("No files");
    }
  }

  function close() {
    if (panelSel) {
      panelSel.remove();
      panelSel = null;
    }
    if (overlaySel) {
      overlaySel.remove();
      overlaySel = null;
    }
    d3.select(window).on("keydown.filePicker", null);
  }

  return {
    open: async (onSelect) => {
      currentOnSelect = typeof onSelect === "function" ? onSelect : (() => {});
      if (!panelSel) await render();
      else {
        currentFiles = await fetchFiles();
        renderList(currentFiles, "");
      }
    },
    close,
    destroy: () => close()
  };
}